import React, { useState } from 'react';
import { SITE_NAME } from '../../config/siteContent';

const TryOnModal = ({ isOpen, onClose, productImage, productName }) => {
  const [personImage, setPersonImage] = useState(null);
  const [personPreview, setPersonPreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  // Validate image file
  const validateImage = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const minSize = 1024; // 1KB

    if (!validTypes.includes(file.type)) {
      return 'Por favor, sube una imagen válida (JPG, PNG o WebP)';
    }

    if (file.size > maxSize) {
      return 'La imagen es demasiado grande. Máximo 10MB permitido';
    }

    if (file.size < minSize) {
      return 'La imagen es demasiado pequeña. Mínimo 1KB requerido';
    }

    return null;
  };

  // Check if image contains a person (basic validation)
  const validatePersonInImage = (imageElement) => {
    return new Promise((resolve) => {
      // Basic aspect ratio and size checks
      const aspectRatio = imageElement.width / imageElement.height;
      
      // Reasonable aspect ratios for person photos (0.5 to 2.0)
      if (aspectRatio < 0.3 || aspectRatio > 3.0) {
        resolve('La imagen debe mostrar una persona completa. Verifica que la foto tenga proporciones adecuadas');
        return;
      }

      // Check minimum dimensions
      if (imageElement.width < 200 || imageElement.height < 200) {
        resolve('La imagen es demasiado pequeña. Usa una imagen de al menos 200x200 píxeles');
        return;
      }

      resolve(null); // Valid
    });
  };

  // Handle person image upload
  const handlePersonImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    const fileError = validateImage(file);
    if (fileError) {
      setError(fileError);
      return;
    }

    setError(null);
    
    try {
      // Create image element for validation
      const img = new Image();
      const reader = new FileReader();
      
      reader.onload = async (event) => {
        img.onload = async () => {
          // Validate person in image
          const personError = await validatePersonInImage(img);
          if (personError) {
            setError(personError);
            return;
          }

          // If all validations pass, set the image
          setPersonImage(file);
          setPersonPreview(event.target.result);
          setError(null);
        };
        
        img.src = event.target.result;
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setError('Error al procesar la imagen. Intenta con otra imagen');
    }
  };

  // Generate virtual try-on
  const handleGenerateTryOn = async () => {
    if (!personImage) {
      setError('Por favor, sube una foto tuya primero');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);
    setProgress(0);
    setProgressMessage('Preparando imágenes...');

    try {
      const formData = new FormData();
      formData.append('personImage', personImage);
      formData.append('productImageUrl', productImage);

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev < 90) {
            const increment = Math.random() * 15;
            const newProgress = Math.min(prev + increment, 90);
            
            if (newProgress < 30) {
              setProgressMessage('Analizando tu imagen...');
            } else if (newProgress < 60) {
              setProgressMessage('Procesando la prenda...');
            } else {
              setProgressMessage('Generando análisis...');
            }
            
            return newProgress;
          }
          return prev;
        });
      }, 500);

      const response = await fetch('/api/tryon/virtual-try-on', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en el servidor');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setProgress(100);
        setProgressMessage('¡Completado!');
        
        // Parse the analysis result
        let analysisResult;
        try {
          analysisResult = JSON.parse(data.data);
        } catch (e) {
          // If it's not JSON, treat as base64 image (fallback)
          analysisResult = { type: 'image', data: data.data };
        }
        
        setResult(analysisResult);
      } else {
        throw new Error(data.error || 'No se pudo generar el análisis');
      }
    } catch (error) {
      console.error('Error:', error);
      
      // Handle different types of network errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('🌐 Sin conexión a internet. Verifica tu conexión y vuelve a intentar.');
      } else if (error.name === 'AbortError') {
        setError('⏱️ La solicitud tardó demasiado. Intenta con imágenes más pequeñas.');
      } else {
        setError('🔧 Error técnico inesperado. Intenta recargar la página.');
      }
    } finally {
      setIsGenerating(false);
      setProgress(0);
      setProgressMessage('');
    }
  };

  // Share functionality
  const handleShare = async (platform) => {
    if (!result) return;

    const shareText = `¡Mira cómo me queda esta ${productName}! Probador virtual de ${SITE_NAME}`;
    const shareUrl = window.location.origin;

    try {
      if (platform === 'whatsapp') {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        window.open(whatsappUrl, '_blank');
      } else if (platform === 'facebook') {
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(facebookUrl, '_blank');
      } else if (platform === 'twitter') {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        window.open(twitterUrl, '_blank');
      } else if (platform === 'copy') {
        await navigator.clipboard.writeText(shareUrl);
        // Show temporary success message
        const originalError = error;
        setError('✅ Enlace copiado al portapapeles');
        setTimeout(() => setError(originalError), 2000);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setError('Error al compartir. Intenta de nuevo.');
    }
  };

  // Download result image
  const handleDownload = () => {
    if (!result) return;

    try {
      const link = document.createElement('a');
      // Handle different result formats
      const imageUrl = result?.image || result?.data || result;
      link.href = imageUrl;
      link.download = `tryon-result-${productName.replace(/\s+/g, '-').toLowerCase()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading:', error);
      setError('Error al descargar la imagen');
    }
  };

  // Reset modal state
  const handleReset = () => {
    setPersonImage(null);
    setPersonPreview(null);
    setResult(null);
    setError(null);
    setProgress(0);
    setProgressMessage('');
  };

  // Close modal and reset
  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="tryon-modal-overlay" onClick={handleClose}>
      <div className="tryon-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="tryon-modal-header">
          <h2 className="tryon-modal-title">
            Probador Virtual
          </h2>
          <button className="tryon-modal-close" onClick={handleClose}>
            <i className="fa-solid fa-times"></i>
          </button>
        </div>

        <div className="tryon-modal-body">
          {!result ? (
            <>
              {/* Upload Section */}
              <div className="tryon-upload-section">
                <div className="tryon-product-preview">
                  <h4>Producto a probar:</h4>
                  <div className="product-info">
                    <img src={productImage} alt={productName} className="product-thumb" />
                    <span className="product-name">{productName}</span>
                  </div>
                </div>

                <div className="tryon-person-upload">
                  <h4>Sube tu foto:</h4>
                  <div className="upload-area">
                    {personPreview ? (
                      <div className="preview-container">
                        <div className="image-preview-wrapper">
                          <img 
                            src={personPreview} 
                            alt="Tu foto" 
                            className="person-preview"
                          />
                          <button
                            onClick={() => {
                              setPersonImage(null);
                              setPersonPreview(null);
                            }}
                            className="edit-overlay-btn"
                            title="Cambiar foto"
                          >
                            <i className="fa-solid fa-edit"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="upload-label">
                        <div className="upload-content">
                          <i className="fa-solid fa-user-plus upload-icon"></i>
                          <p>Haz clic para subir tu foto</p>
                          <small>JPG, PNG hasta 10MB</small>
                        </div>
                        <input
                          type="file"
                          className="upload-input"
                          accept="image/*"
                          onChange={handlePersonImageUpload}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="tryon-error">
                  <i className="fa-solid fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Generate Button */}
              <div className="tryon-actions">
                {isGenerating && (
                  <div className="progress-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="progress-message">{progressMessage}</div>
                  </div>
                )}
                
                <button 
                  onClick={handleGenerateTryOn}
                  disabled={!personImage || isGenerating}
                  className={`tryon-generate-btn ${(!personImage || isGenerating) ? 'disabled' : ''}`}
                >
                  <i class="fa-solid fa-wand-magic-sparkles me-2 "> </i>
                  {isGenerating ? (
                    <>
                      <div className="spinner"></div>
                      <span style={{ marginLeft: '8px' }}>Generando imagen...</span>
                    </>
                  ) : (
                    'Probar ahora'
                  )}
                </button>
              </div>
            </>
          ) : (
            /* Result Section */
            <div className="tryon-result">
              
              {result?.type === 'analysis' ? (
                <div className="analysis-result">
                  <div className="analysis-header">
                    <i className="fa-solid fa-search me-2"></i>
                    <span>{result.message}</span>
                  </div>
                  <div className="analysis-content">
                    <p>{result.description}</p>
                  </div>
                </div>
              ) : (
                <div className="image-result">
                  <img 
                    src={result?.image || result?.data || result} 
                    alt="Resultado del probador virtual" 
                    className="result-image"
                  />
                 
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="result-actions">
                {result?.type !== 'analysis' && (
                  <button
                    onClick={handleDownload}
                    className="download-btn"
                  >
                    <i className="fa-solid fa-download me-2"></i>
                    Descargar
                  </button>
                )}
                <button
                  onClick={handleReset}
                  className="try-again-btn"
                >
                  <i className="fa-solid fa-redo me-2"></i>
                  Probar de Nuevo
                </button>
              </div>

              {/* Social Share Section */}
              <div className="share-section">
                <h4 className="share-title">
                  <i className="fa-solid fa-share-nodes me-2"></i>
                  Compartir Resultado
                </h4>
                <div className="share-buttons">
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="share-btn whatsapp"
                    title="Compartir en WhatsApp"
                  >
                    <i className="fa-brands fa-whatsapp"></i>
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="share-btn facebook"
                    title="Compartir en Facebook"
                  >
                    <i className="fa-brands fa-facebook"></i>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="share-btn twitter"
                    title="Compartir en Twitter"
                  >
                    <i className="fa-brands fa-twitter"></i>
                  </button>
                  <button
                    onClick={() => handleShare('copy')}
                    className="share-btn copy"
                    title="Copiar enlace"
                  >
                    <i className="fa-solid fa-link"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TryOnModal;
